import { useEffect, useState } from 'react';
import { HeartHandshake } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze, zalozkyModulu } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { SKLO_SVETLE } from '../lib/uiTokens';
import ZakazniciSeznam from '../components/zakaznici/ZakazniciSeznam';
import KomunikaceModul from '../components/zakaznici/KomunikaceModul';
import VernostModul from '../components/zakaznici/VernostModul';
import KampaneModul from '../components/zakaznici/KampaneModul';
import { SECTION_COLOR, SECTION_BG } from '../components/objednavky/ui';

/**
 * Modul Zákazníci (sekce Obchod, id `zakaznici`) — od 2026-10-12.
 *
 * Do té doby záložka modulu Hosté (`objednavky.zakaznici`), předtím modul
 * `zakaznicky-zazitek`. Hosté se rozdělili podle práce: Objednávky řeší
 * provoz dne (objednávky a rezervace), Zákazníci vztah s hostem v čase.
 * Návrh je v dokumentu „Návrh: rozdělení Hostů a nový modul Zákazníci";
 * záložka Přehled (grafy ze snímků) zatím chybí.
 *
 * Záložky (klíče přístupů):
 *  - Zákazníci (`zakaznici.seznam`) — seznam, karta hosta, segmenty
 *  - Komunikace (`zakaznici.komunikace`) — schránka hovorů a SMS telefonu
 *    bistra, vlákno s číslem, vyřízení (od 2026-10-12); proklik `{ tab:
 *    'komunikace', cislo }`
 *  - Věrnost (`zakaznici.vernost`) — členové podle úrovní FofrTýmu, body
 *  - Kampaně (`zakaznici.kampane`) — kampaně, slevové kódy, šablony (příprava)
 *
 * Proklik z jiného modulu: `navigateTo('zakaznici', { tab: 'seznam', id })`.
 * Staré adresy (`zakaznicky-zazitek`, `objednavky` + `tab: 'zakaznici'`)
 * přesměruje `app.jsx`.
 */
export default function Zakaznici({ user, initialTab = null, navParams = null, onNavigate }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile] = useState(() => window.innerWidth < 768);

    const povolene = zalozkyModulu('zakaznici', user).map(z => z.id);
    const [tab, setTab] = useState(() => (initialTab && povolene.includes(initialTab) ? initialTab : povolene[0] ?? 'seznam'));
    const [otevrit, setOtevrit] = useState(() => (navParams?.id ? { tab: navParams.tab || tab, id: navParams.id } : null));
    useEffect(() => {
        if (initialTab && povolene.includes(initialTab)) setTab(initialTab);
        setOtevrit(navParams?.id ? { tab: initialTab || tab, id: navParams.id } : null);
    }, [initialTab, navParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const prepnout = (t) => { setTab(t); setOtevrit(null); setKomunikaceCislo(null); };
    // Z karty hosta do vlákna hovorů a SMS — jen když na Komunikaci je přístup
    const [komunikaceCislo, setKomunikaceCislo] = useState(null);
    const naKomunikaci = povolene.includes('komunikace') ? (cislo) => { setKomunikaceCislo(cislo); setTab('komunikace'); } : null;

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    // Na mobilu připnuté k výřezu (norma §1b), na desktopu na výšku obsahu
    const pripnuta = isMobile
        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
        : { height: '100%', minHeight: 0 };

    // Objednávky a rezervace žijí v modulu Objednávky — proklik tam, když na ně člověk má přístup
    const naObjednavku = muze(user, 'objednavky.zive') && onNavigate ? (id) => onNavigate('objednavky', { tab: 'zive', id }) : null;
    const naRezervaci = muze(user, 'objednavky.rezervace') && onNavigate ? (r) => onNavigate('objednavky', { tab: 'rezervace', id: r.id, datum: r.datum }) : null;
    const otevritId = otevrit?.tab === tab ? otevrit.id ?? null : null;

    const spolecne = { isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole: isMobile ? REZERVA_LISTY : null };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <HeartHandshake size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Zákazníci</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Karta hosta, komunikace, věrnost a kampaně</p>
                        </div>
                    </div>
                )}
                <TabBar modul="zakaznici" user={user} aktivni={tab} onZmena={prepnout} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                <div className="flex flex-col" style={{ ...pripnuta, flex: isMobile ? undefined : 1 }}>
                    {tab === 'seznam' && (
                        <ZakazniciSeznam {...spolecne} otevritId={otevritId} onObjednavka={naObjednavku} onRezervace={naRezervaci} onKomunikace={naKomunikaci} />
                    )}
                    {tab === 'komunikace' && (
                        <KomunikaceModul {...spolecne} otevritCislo={komunikaceCislo ?? (navParams?.tab === 'komunikace' ? navParams?.cislo ?? null : null)}
                            onZakaznik={(id) => { setTab('seznam'); setOtevrit({ tab: 'seznam', id }); }} onObjednavka={naObjednavku} />
                    )}
                    {tab === 'vernost' && (
                        <VernostModul {...spolecne} onObjednavka={naObjednavku} onRezervace={naRezervaci} onKomunikace={naKomunikaci} />
                    )}
                    {tab === 'kampane' && <KampaneModul {...spolecne} />}
                </div>
            </div>
        </div>
    );
}
